import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { api, type CronJob, type Client, type Node } from '../lib/api'
import { PageHeader, Button, Table, Modal, Field, Input, Select, ErrorBox, Badge, Spinner } from '../components/ui'
import { IconPlus } from '../components/icons'
import { RowMenu } from '../components/RowMenu'
import { TaskProgress } from '../components/TaskProgress'
import { FilterBar, FilterSelect, SearchInput } from '../components/Filters'

export function CronPage() {
  const qc = useQueryClient()
  const [addOpen, setAddOpen] = useState(false)
  const [taskId, setTaskId] = useState<string | null>(null)

  const { data: jobs = [] } = useQuery({ queryKey: ['cron-jobs'], queryFn: async () => (await api.get<CronJob[]>('/cron-jobs')).data })
  const { data: clients = [] } = useQuery({ queryKey: ['clients'], queryFn: async () => (await api.get<Client[]>('/clients')).data })
  const { data: nodes = [] } = useQuery({ queryKey: ['nodes'], queryFn: async () => (await api.get<Node[]>('/nodes')).data })
  const clientMap = Object.fromEntries(clients.map(c => [c.id, c]))
  const nodeMap = Object.fromEntries(nodes.map(n => [n.id, n]))

  const [fNode, setFNode] = useState('')
  const [fClient, setFClient] = useState(0)
  const [fSearch, setFSearch] = useState('')
  const filtered = useMemo(() => {
    let list = jobs
    if (fNode) list = list.filter(j => j.node_id === fNode)
    if (fClient) list = list.filter(j => j.client_id === fClient)
    if (fSearch) {
      const q = fSearch.toLowerCase()
      list = list.filter(j => j.command.toLowerCase().includes(q) || j.schedule.includes(q))
    }
    return list
  }, [jobs, fNode, fClient, fSearch])

  const del = useMutation({
    mutationFn: async (id: number) => api.delete(`/cron-jobs/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cron-jobs'] }),
  })
  const [historyFor, setHistoryFor] = useState<CronJob | null>(null)
  const runNow = useMutation({
    mutationFn: async (id: number) => (await api.post<{ task_id: string }>(`/cron-jobs/${id}/run`)).data,
  })
  const toggleActive = useMutation({
    mutationFn: async (j: CronJob) => (await api.post(`/cron-jobs/${j.id}/${j.is_active ? 'suspend' : 'resume'}`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['cron-jobs'] }),
  })

  return (
    <>
      <PageHeader
        title="Cron"
        subtitle="Периодические задачи. Синхронизируются в crontab -u <клиент> автоматически."
        actions={<Button onClick={() => setAddOpen(true)}><IconPlus /> Добавить задачу</Button>}
      />
      <FilterBar>
        <SearchInput value={fSearch} onChange={setFSearch} placeholder="Команда / расписание…" className="min-w-[240px] flex-1" />
        <FilterSelect value={fNode} onChange={setFNode} options={[
          { value: '', label: 'Все ноды' },
          ...nodes.map(n => ({ value: n.id, label: n.name })),
        ]} />
        <FilterSelect value={fClient} onChange={setFClient} options={[
          { value: 0, label: 'Все клиенты' },
          ...clients.map(c => ({ value: c.id, label: c.unix_user })),
        ]} />
      </FilterBar>
      <Table<CronJob>
        rows={filtered} empty={jobs.length === 0 ? 'Нет запланированных задач.' : 'Ничего не найдено.'}
        cols={[
          { key: 's', label: 'Расписание', render: (j) => <span className="font-mono text-[12px] font-medium">{j.schedule}</span> },
          { key: 'nd', label: 'Нода', render: (j) => <span className="text-[13px] text-neutral-700">{nodeMap[j.node_id]?.name || '—'}</span> },
          { key: 'c', label: 'Клиент', render: (j) => <span className="font-mono text-[12px]">{clientMap[j.client_id]?.unix_user || j.client_id}</span> },
          { key: 'cmd', label: 'Команда', render: (j) => <span className="font-mono text-[12px] text-neutral-700 break-all">{j.command}</span> },
          { key: 'n', label: 'Уведомление', render: (j) => <span className="text-[12px] text-neutral-500">{j.notify_email || '—'}</span> },
          { key: 'st', label: '', render: (j) => j.is_active ? <Badge tone="green">активна</Badge> : <Badge>выключена</Badge> },
          { key: 'act', label: '', w: '1%', render: (j) => (
            <div className="flex justify-end">
              <RowMenu items={[
                { label: 'Запустить сейчас', onClick: () => runNow.mutate(j.id) },
                { label: 'История запусков', onClick: () => setHistoryFor(j) },
                j.is_active
                  ? { label: 'Отключить', onClick: () => toggleActive.mutate(j) }
                  : { label: 'Включить', onClick: () => toggleActive.mutate(j) },
                { divider: true, label: '', onClick: () => {} },
                { label: 'Удалить', danger: true,
                  onClick: () => confirm('Удалить задачу?') && del.mutate(j.id) },
              ]} />
            </div>
          )},
        ]}
      />
      {addOpen && <AddCronModal clients={clients} onClose={() => setAddOpen(false)}
        onTask={(t) => { setAddOpen(false); setTaskId(t); qc.invalidateQueries({ queryKey: ['cron-jobs'] }) }} />}
      {taskId && <TaskProgress taskId={taskId} onClose={() => setTaskId(null)} />}
      {historyFor && <CronHistoryModal job={historyFor} onClose={() => setHistoryFor(null)} />}
    </>
  )
}

interface CronRun {
  id: number; started_at: string; finished_at?: string
  exit_code?: number; stdout: string; stderr: string
}

function CronHistoryModal({ job, onClose }: { job: CronJob; onClose: () => void }) {
  const { data = [], isLoading, refetch } = useQuery({
    queryKey: ['cron-history', job.id],
    queryFn: async () => (await api.get<CronRun[]>(`/cron-jobs/${job.id}/history`)).data,
    refetchInterval: 5000,
  })
  return (
    <Modal open onClose={onClose} title={`История: ${job.command.slice(0, 60)}`} size="lg">
      {isLoading ? <div className="p-8"><Spinner /></div> : (
        <div className="max-h-[500px] overflow-y-auto">
          {data.length === 0 && <div className="p-8 text-center text-neutral-400">Нет запусков. Нажми «Запустить сейчас» в меню задачи.</div>}
          {data.map(r => (
            <div key={r.id} className="border-b border-neutral-100 last:border-0 py-3">
              <div className="flex items-center gap-3 mb-2">
                <Badge tone={r.exit_code === 0 ? 'green' : r.finished_at ? 'red' : 'blue'}>
                  {r.finished_at ? (r.exit_code === 0 ? '✓ ok' : `exit ${r.exit_code}`) : 'выполняется'}
                </Badge>
                <span className="text-[12px] text-neutral-500 font-mono">{new Date(r.started_at).toLocaleString('ru-RU')}</span>
              </div>
              {(r.stdout || r.stderr) && (
                <pre className="bg-neutral-900 text-neutral-100 text-[11px] font-mono p-3 rounded-md overflow-x-auto max-h-40">
{r.stdout || '(no stdout)'}
{r.stderr && '\n---stderr---\n' + r.stderr}
                </pre>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="flex justify-between pt-3 border-t border-neutral-100 mt-3">
        <Button variant="secondary" onClick={() => refetch()}>↻ Обновить</Button>
        <Button variant="ghost" onClick={onClose}>Закрыть</Button>
      </div>
    </Modal>
  )
}

function AddCronModal({ clients, onClose, onTask }: { clients: Client[]; onClose: () => void; onTask: (id: string) => void }) {
  const [f, setF] = useState({ client_id: clients[0]?.id || 0, schedule: '*/5 * * * *', command: '', notify_email: '' })
  const [err, setErr] = useState('')
  const create = useMutation({
    mutationFn: async () => (await api.post('/cron-jobs', f)).data,
    onSuccess: (r: any) => onTask(r.task_id),
    onError: (e: any) => setErr(e?.response?.data?.detail || e.message),
  })
  return (
    <Modal open onClose={onClose} title="Добавить cron-задачу">
      <form onSubmit={(e) => { e.preventDefault(); create.mutate() }} className="space-y-4">
        <Field label="Клиент">
          <Select value={f.client_id} onChange={(e) => setF({ ...f, client_id: +e.target.value })}>
            {clients.map(c => <option key={c.id} value={c.id}>{c.unix_user}</option>)}
          </Select>
        </Field>
        <Field label="Расписание">
          <div className="flex gap-2 flex-wrap mb-2">
            {CRON_PRESETS.map(p => (
              <button key={p.expr} type="button"
                      onClick={() => setF({ ...f, schedule: p.expr })}
                      className={`px-2.5 py-1 text-[12px] rounded-md border transition ${
                        f.schedule === p.expr
                          ? 'bg-black text-white border-black'
                          : 'bg-white border-neutral-300 text-neutral-700 hover:border-neutral-500'
                      }`}>{p.label}</button>
            ))}
          </div>
          <Input value={f.schedule} onChange={(e) => setF({ ...f, schedule: e.target.value })} className="font-mono" required />
          <div className="text-[12px] text-neutral-500 mt-1">
            {validateCron(f.schedule)
              ? <span className="text-green-700">✓ {describeCron(f.schedule)}</span>
              : <span className="text-red-600">✗ Невалидное cron-выражение</span>}
          </div>
        </Field>
        <Field label="Команда"><Input value={f.command} onChange={(e) => setF({ ...f, command: e.target.value })} placeholder="php /var/www/user/data/www/site.com/cron.php" className="font-mono" required /></Field>
        <Field label="Уведомление на email (опционально)"><Input type="email" value={f.notify_email} onChange={(e) => setF({ ...f, notify_email: e.target.value })} /></Field>
        {err && <ErrorBox>{err}</ErrorBox>}
        <div className="flex justify-end gap-2 pt-4 border-t border-neutral-100 -mx-5 px-5">
          <Button variant="ghost" onClick={onClose}>Отмена</Button>
          <Button type="submit" disabled={create.isPending}>Создать</Button>
        </div>
      </form>
    </Modal>
  )
}

const CRON_PRESETS: { label: string; expr: string }[] = [
  { label: 'Каждую минуту',   expr: '* * * * *' },
  { label: 'Каждые 5 мин',    expr: '*/5 * * * *' },
  { label: 'Каждые 15 мин',   expr: '*/15 * * * *' },
  { label: 'Каждый час',      expr: '0 * * * *' },
  { label: 'Каждые 6 часов',  expr: '0 */6 * * *' },
  { label: 'Ежедневно 03:00', expr: '0 3 * * *' },
  { label: 'Еженедельно',     expr: '0 3 * * 0' },
  { label: 'Ежемесячно',      expr: '0 3 1 * *' },
]

function validateCron(s: string): boolean {
  const parts = s.trim().split(/\s+/)
  if (parts.length !== 5) return false
  const ranges: [number, number][] = [[0, 59], [0, 23], [1, 31], [1, 12], [0, 6]]
  for (let i = 0; i < 5; i++) {
    if (!validateCronField(parts[i], ranges[i][0], ranges[i][1])) return false
  }
  return true
}
function validateCronField(f: string, min: number, max: number): boolean {
  if (f === '*') return true
  for (const part of f.split(',')) {
    const stepMatch = part.match(/^(.+?)\/(\d+)$/)
    let range = part
    if (stepMatch) {
      range = stepMatch[1]
      if (parseInt(stepMatch[2]) <= 0) return false
    }
    if (range === '*') continue
    if (range.includes('-')) {
      const [a, b] = range.split('-').map(Number)
      if (isNaN(a) || isNaN(b) || a < min || b > max || a > b) return false
    } else {
      const n = Number(range)
      if (isNaN(n) || n < min || n > max) return false
    }
  }
  return true
}
function describeCron(s: string): string {
  const preset = CRON_PRESETS.find(p => p.expr === s)
  if (preset) return preset.label.toLowerCase()
  const parts = s.trim().split(/\s+/)
  if (parts.length !== 5) return s
  const [min, hour, dom, mon, dow] = parts
  const out: string[] = []
  if (min === '*') out.push('каждую минуту')
  else if (min.startsWith('*/')) out.push(`каждые ${min.slice(2)} мин`)
  else out.push(`в ${min} мин`)
  if (hour !== '*') out.push(`в ${hour}ч`)
  if (dom !== '*') out.push(`числа ${dom}`)
  if (mon !== '*') out.push(`месяца ${mon}`)
  if (dow !== '*') out.push(`день недели ${dow}`)
  return out.join(', ')
}
